import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material";
import { HelmetProvider } from "react-helmet-async";

import AuthContext from "./contexts/AuthContext";
import { AppContext } from "./contexts/AppContext";

import Auth from "./pages/Auth";
import Event from "./pages/Auth/components/Event";
import Layout from "./components/Layout";
import Profile from "./pages/Profile";
import Matchmaking from "./pages/Matchmaking/Matchmaking";
import Events from "./pages/Events";
import Ticketing from "./pages/Ticketing";
import Checkout from "./pages/Checkout";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import MyEvents from "./pages/Events/components/MyEvents";
import ActiveEvent from "./pages/ActiveEvent/ActiveEvent";
import EventResults from "./pages/Events/components/EventResults";
import ContactUs from "./pages/ContactUs";
import Privacy from "./pages/Privacy/Privacy";
import MessageDialog from "./components/MessageDialog";
import NoShowForm from "./pages/NoShowForm";
import DinnerForm from "./pages/DinnerForm";
import Vote from "./pages/Vote";

const Router = ({ showError, setShowError }) => {
  const { signedIn, appStyle, appConfig, socket, setPromptData, orgId } =
    React.useContext(AuthContext);
  const [event, setActiveEvent] = React.useState(null);
  const [events, setEvents] = React.useState([]);
  const [myEvents, setMyEvents] = React.useState([]);
  const [mixerAttendees, setMixerAttendees] = React.useState([]);

  const theme = createTheme({
    palette: {
      primary: {
        main: appStyle?.primaryColor ? appStyle.primaryColor : "#ef5241",
      },
    },
    typography: {
      fontFamily: `"Bitter", serif`,
    },
  });

  React.useEffect(() => {
    console.log(`Socket update: ${socket}`);
  }, [socket]);

  React.useEffect(() => {
    if (event && socket && socket.connected) {
      socket.emit("joinEvent", event._id);
      console.log("Joined event room");

      const promptHandler = (data) => {
        console.log("prompt received", data);
        setPromptData(data);

        try {
          if (Notification.permission === "granted") {
            new Notification("New Prompt!", {
              body: data.message || "Check out your new prompt!",
              // icon:appStyle ? appStyle.logo : null
            });
          } else if (Notification.permission !== "denied") {
            Notification.requestPermission().then((permission) => {
              if (permission === "granted") {
                new Notification("New Prompt!", {
                  body: data.message || "Check out your new prompt!",
                  // icon:appStyle ? appStyle.logo : null
                });
              }
            });
          }
        } catch (e) {
          console.error("Prompt notifications failed");
        }
      };

      socket.on("showPrompt", promptHandler);

      // Cleanup to remove duplicate listener on re-render/unmount
      return () => {
        socket.off("showPrompt", promptHandler);
      };
    }
  }, [event, socket, setPromptData]);

  if (signedIn) {
    return (
      <BrowserRouter>
        <AppContext.Provider
          value={{
            event,
            setActiveEvent,
            // socket,
            events,
            setEvents,
            myEvents,
            setMyEvents,
            mixerAttendees,
            setMixerAttendees,
          }}
        >
          <Layout>
            <Routes>
              <Route index path="/" element={<MyEvents />} />
              <Route path="/events" element={<Events />} />
              <Route path="/tickets/:eventId" element={<Ticketing />} />
              <Route path="/checkout/:eventId" element={<Checkout />} />
              <Route
                path="/checkout/success/:eventId/:paymentIntentId"
                element={<CheckoutSuccess />}
              />
              <Route path="/profile" element={<Profile />} />
              <Route path="/matchmaking" element={<Matchmaking />} />
              <Route path="/activeevent" element={<ActiveEvent />} />
              <Route path="/activeevent/:eventId" element={<ActiveEvent />} />
              <Route path="/eventresults/" element={<EventResults />} />
              <Route path="/eventresults/:id" element={<EventResults />} />
              <Route path="/contactus" element={<ContactUs />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/noshow" element={<NoShowForm />} />
              <Route path="/noshow/:eventId" element={<NoShowForm />} />
              <Route path="/noshow/:eventId/:email" element={<NoShowForm />} />
              <Route path="/dinner" element={<DinnerForm />} />
              <Route path="/vote/:eventId" element={<Vote />} />
              <Route path="/pitch/:eventId" element={<Vote />} />
            </Routes>
          </Layout>
        </AppContext.Provider>
      </BrowserRouter>
    );
  }

  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <Routes>
            <Route
              path="/"
              element={<Auth config={appConfig} orgId={orgId} />}
            />
            <Route
              path="/browse"
              element={
                <Auth defaultTab="browse" config={appConfig} orgId={orgId} />
              }
            />
            <Route path="/event/:slug/:eventId" element={<Event />} />
            <Route path="/tickets/:eventId" element={<Event />} />

            <Route
              path="/eventresults/:id"
              element={<Navigate to="/" replace />}
            />
            <Route path="/noshow" element={<NoShowForm />} />
            <Route path="/noshow/:eventId" element={<NoShowForm />} />
            <Route path="/noshow/:eventId/:email" element={<NoShowForm />} />
            <Route path="/dinner" element={<DinnerForm />} />
            <Route path="/vote/:eventId" element={<Vote />} />
            <Route path="/pitch/:eventId" element={<Vote />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <MessageDialog
            open={showError}
            close={() => setShowError(false)}
            title="Error loading page"
            body="This page did not load properly. Please check your connection and try refreshing."
          />
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
};

export default Router;
